import { makeId, setAttributes, stringifyForAttribute } from '@kissui/helpers/src/utils'
import createProps from '@kissui/helpers/src/props.helpers'
import { dispatchEvent } from '@kissui/helpers/src/assets/js/eventDispatch'
import viewportHelper from '@kissui/helpers/src/viewport.helpers'
import { EVENT_POPIN_OPEN, EVENT_CTA_CLICK, CONTRAST_DARK } from '@kissui/components'
import { handlePromoClick } from '@kissui/helpers/src/gtmEvents'

import './components/countdown'
import '@kissui/components/src/popin'
import '@kissui/components/src/video'

class HeroBanner extends HTMLElement {
    constructor() {
        super()
        this.isMobile = false
        this.popinId = `video_${makeId(10)}`
        this.hasVideo = false
        this.hasSelfHostedVideo = false
        this.headingCountdown = false
        this.descCountdown = false
        this.countdownEnded = false
        this.boundOnLinkClick = this.onLinkClick.bind(this)
        this.boundOnOpenPlayer = this.onOpenPlayer.bind(this)
        this.boundhandlePromoClick = this.handleClick.bind(this)
        this.boundOnButtonCtaClick = this.onButtonCtaClick.bind(this)
        this.boundOnButtonSelfVideoClick = this.boundOnButtonSelfVideoClick.bind(this)

        this.renderSelfVideoButton = null
        this.videoButton = null
        this.link = null
        this.videoHTMLEle = null
        this.buttonCta = null
        this.videoPopinElement = null
    }

    connectedCallback() {
        this.props = createProps(this.attributes)
        this.hasSelfHostedVideo = !!this.props.layout?.background_video?.desktop
        this.classes = this.createClasses()
        this.render()
        this.countdownRerender()
    }

    createClasses() {
        const { padding_top, padding_bottom } = this.props.layout
        const base = 'nb-hero-banner'

        const fullHeight =
            this.props.layout.background_video?.fullHeight === 'true' ||
            this.props.layout.background_video?.fullHeight === true
                ? 'fullHeight'
                : ''

        return {
            base,
            videoCta: base + '__video',
            videoCtaIcon: base + '__videoIcon',
            hasSelfHostedVideo: this.hasSelfHostedVideo
                ? base + '__selfHostedVideo' + ' ' + fullHeight
                : '',
            container: [base, padding_top, padding_bottom].join(' ')
        }
    }

    getContrast() {
        let { contrast, contrast_mobile, contrastMobile = null } = this.props.layout

        // Retro compatibility
        if (contrastMobile) {
            contrast_mobile = contrastMobile
        }

        if (viewportHelper.is.mobile && contrast_mobile !== '') {
            // manages responsive contrast at the custom element level
            this.setAttribute('contrast', contrast_mobile)
        }

        return viewportHelper.is.mobile && contrast_mobile !== '' ? contrast_mobile : contrast
    }

    getTextAlignment() {
        let { textAlign = null } = this.props.layout

        if ((viewportHelper.is.desktop || viewportHelper.is.retina) && textAlign !== '') {
            return textAlign
        }
    }

    getColor() {
        let result = 'black'

        if (this.getContrast() === CONTRAST_DARK) {
            result = 'white'
        }

        return result
    }

    render() {
        const { layout, campaign, copywriting } = this.props
        const {
            background_color,
            background = {
                retina: '',
                desktop: '',
                mobile: ''
            }
        } = layout
        const { video = {}, text_countdown = {} } = copywriting
        this.descCountdown = !!text_countdown?.date
        this.hasVideo = !!(video.video_id && video.video_service)

        let renderSelfVideoMobile = ''
        let renderSelfVideoDesktop = ''
        const backgroundRetina = background.retina || ''
        const backgroundDesktop = background.desktop || ''
        const backgroundMobile = background.mobile || ''

        if (this.hasSelfHostedVideo) {
            if (viewportHelper.is.mobile) {
                renderSelfVideoMobile = this.renderSelfVideoBackground()
            } else {
                renderSelfVideoDesktop = this.renderSelfVideoBackground()
            }
        }

        this.innerHTML = `
            <nb-container
                background_color="${background_color}"
                background_retina="${backgroundRetina}"
                background_desktop="${backgroundDesktop}"
                background_mobile="${backgroundMobile}"
                campaign_id="${campaign.id}"
                campaign_name="${campaign.name}"
                campaign_position="${campaign.position}"
                campaign_creative="${campaign.creative}"
                contrast="${this.getContrast()}"
                classname="${this.classes.container} ${this.classes.hasSelfHostedVideo}"
                lazy_load="false"

            >
              <div class="${
                  this.classes.base
              }__centerContainer" text-align="${this.getTextAlignment()}">
                <div class="${this.classes.base}__content">
                    <div class="${this.classes.base}__content__top">
                        ${this.renderTitle()}
                        ${this.renderImage()}
                        ${this.renderDescription()}
                    </div>
                    ${renderSelfVideoMobile}
                    <div class="${this.classes.base}__content__bottom">
                        <div class="${this.classes.base}__ctasWrapper">
                            ${this.renderCta()}
                            ${this.renderCtaSecondary()}
                        </div>
                        ${this.renderVideoCta()}
                        ${this.renderLink()}
                        ${this.renderVideoBackground()}
                    </div>
                </div>
              </div>
              ${renderSelfVideoDesktop}
          </nb-container>
          `

        this.bindEvents()
    }

    renderPlayer() {
        if (!this.hasVideo) {
            return ''
        }

        const {
            video_id,
            video_mobile = '',
            display_ui_controls,
            cc_policy_language,
            video_service
        } = this.props.copywriting.video

        this.videoPopinElement = document.createElement('nb-popin')

        setAttributes(this.videoPopinElement, {
            id: this.popinId,
            popin_id: this.popinId,
            variation: 'before',
            bgcolor: 'white'
        })

        this.videoElement = document.createElement('nb-video')
        setAttributes(this.videoElement, {
            container_id: 'player-' + this.popinId,
            video_id: video_id,
            video_mobile: video_mobile,
            video_service: video_service,
            popin_id: this.popinId,
            display_controls: display_ui_controls,
            ccPolicy_language: cc_policy_language,
            allow_popin_autoplay: 'true'
        })
        this.videoPopinElement.appendChild(this.videoElement)
        this.appendChild(this.videoPopinElement)
    }
    renderSelfVideoBackground() {
        if (!this.hasSelfHostedVideo || !this.props.layout.background_video) {
            return ''
        }

        const { labelPause, labelPlay, mobilePoster, desktopPoster, mobile, desktop } =
            this.props.layout.background_video

        const video_url = viewportHelper.is.mobile ? mobile : desktop
        const video_poster_url = viewportHelper.is.mobile ? mobilePoster : desktopPoster
        const styleInline = viewportHelper.is.mobile ? 'position: relative' : ''

        return `
            <div class="video__container" style="${styleInline}">
                <div class="video__controls__container">
                    <video id="video_selfHosted" class="${this.classes.base}__visual" preload="metadata" poster="${video_poster_url}" playsinline autoplay muted loop>
                        <source src="${video_url}" type="video/mp4">
                    </video>
                    <button id="video__play__centerButton" class="video_button icon-pause" aria-pressed="false" data-status="pause">
                        <span class="video_button__icon">
                            <nb-icon icon="24/symbol/play-solid" class="playButton" alt="${labelPlay}"></nb-icon>
                            <nb-icon icon="24/symbol/pause-solid" class="pauseButton" alt="${labelPause}"></nb-icon>
                            <p class="sr-only video_button">${labelPause}</p>
                        </span>
                    </button>
                </div>
            </div>`
    }

    renderVideoBackground() {
        const { video } = this.props.copywriting

        if (!this.hasVideo || viewportHelper.is.mobile || !video.auto_play) {
            return ''
        }

        this.props.copywriting.video.mute = true
        this.createVideoElement()

        return this.videoElement.outerHTML
    }

    createVideoElement() {
        this.videoElement = document.createElement('nb-video')
        setAttributes(this.videoElement, {
            ...this.props.copywriting.video,
            container_id: `player-${this.popinId}`,
            popin_id: this.popinId,
            allow_popin_autoplay: 'true'
        })
    }

    countdownRerender() {
        if (!this.headingCountdown && !this.descCountdown) {
            return ''
        }

        const eleCountdown = this.querySelector('nb-countdown')
        if (!eleCountdown) {
            return ''
        }

        this.countdownEnded = eleCountdown.getAttribute('countdown-end') === 'true'
        if (this.countdownEnded) {
            this.render()
        }
    }

    renderTitle() {
        const {
            overline,
            heading,
            heading_countdown = {},
            image_logo,
            image_logo_max_width,
            image_logo_alt
        } = this.props.copywriting
        this.headingCountdown = !!heading_countdown?.date

        this.overLineRender = ''
        if (overline) {
            this.overLineRender = `<h1 class="t-md-700-sl ${this.classes.base}__overline">${overline}</h1>`
        }

        this.imageLogoRender = ''
        if (image_logo) {
            let imageMaxWidth = ''
            if (image_logo_max_width) {
                imageMaxWidth = `style="max-width:${image_logo_max_width}"`
            }

            this.imageLogoRender = `<div class=${this.classes.base}__imageLogo>
                        <img class=${this.classes.base}__imageLogoAsset
                            src=${image_logo}
                            alt=${image_logo_alt}
                            fetchpriority="high"
                            ${imageMaxWidth} />
                        </div>`
        }

        this.headingRender = ''

        if (heading) {
            const headingCountdown = this.countdownEnded
                ? heading_countdown.heading
                : heading + this.renderCountdown(heading_countdown)

            this.headingRender = `<h2 class='${
                this.hasSelfHostedVideo ? 'h-2xl-700' : 'h-3xl-700'
            } ${this.classes.base}__title'>${
                this.headingCountdown ? headingCountdown : heading
            }</h2>`
        }

        return `
            ${this.overLineRender}
            ${this.imageLogoRender}
            ${this.headingRender}
            `
    }

    renderImage() {
        if (!this.props.copywriting.image || !viewportHelper.is.mobile) {
            return ''
        }

        const { copywriting } = this.props
        const { image, image_alt } = copywriting

        const className = `${this.classes.base}__imageCenteredMobile`
        const classNameImg = `${this.classes.base}__image`

        return `<div class="${className}">
                    <img class="${classNameImg}" src=${image} alt="${image_alt}" fetchpriority="high" />
                    ${this.hasVideo ? this.renderVideoCtaIcon() : ''}
                </div>`
    }

    renderDescription() {
        const { copywriting } = this.props
        const { text = '', text_countdown = {} } = copywriting

        if (!text) {
            return ''
        }

        const headingCountdown = this.countdownEnded
            ? text_countdown.text
            : `${text} ${this.renderCountdown(text_countdown)}`

        return `<div class='t-md-400-sl ${this.classes.base}__text'>${
            this.descCountdown ? headingCountdown : text
        }</div>`
    }

    renderCountdown(countdown) {
        const { date, time, locale } = countdown

        return `<nb-countdown date="${date}" time="${time}" locale=${JSON.stringify(
            locale
        )}></nb-countdown>`
    }

    renderVideoCta() {
        const { cta = {} } = this.props.copywriting
        const { label = '' } = cta

        if (!this.hasVideo || label === '') {
            return ''
        }

        const ctaVideoClass = this.classes.videoCta
        const data = stringifyForAttribute(cta)

        return `<nb-cta
                icon_right="24/symbol/play-solid"
                class="${this.classes.base}__cta ${ctaVideoClass}"
                data="${data}"
            >${label}</nb-cta>`
    }

    renderVideoCtaIcon() {
        const { cta = {} } = this.props.copywriting
        const { label = '' } = cta

        if (!this.hasVideo || label === '') {
            return ''
        }

        const ctaVideoClass = this.classes.videoCtaIcon
        const data = stringifyForAttribute(cta)

        return `<nb-cta
                icon_right="24/symbol/play-solid"
                class="${this.classes.base}__videoIcon ${ctaVideoClass}"
                data="${data}"
                tracking_label="Page Builder - Hero Banner - Click CTA - Play video"
            ></nb-cta>`
    }

    renderCta() {
        const { campaign, copywriting } = this.props
        const { cta = {}, heading_countdown = {}, text_countdown = {} } = copywriting
        const { label = '' } = cta

        if (this.hasVideo || label === '') {
            return ''
        }

        if (this.countdownEnded) {
            if (this.descCountdown) {
                cta.link = text_countdown.cta_link
            } else if (this.headingCountdown) {
                cta.link = heading_countdown.cta_link
            }
        }

        cta.campaign_id = campaign.id || ''
        cta.campaign_name = campaign.name || ''
        cta.campaign_position = campaign.position || ''
        cta.campaign_creative = campaign.creative || ''

        const data = stringifyForAttribute(cta)

        return `<nb-cta class="${this.classes.base}__cta" data="${data}">${label}</nb-cta>`
    }

    renderCtaSecondary() {
        const { campaign, copywriting } = this.props
        const { cta_secondary = {} } = copywriting
        const { label = '' } = cta_secondary

        if (label === '') {
            return ''
        }

        cta_secondary.campaign_id = campaign.id || ''
        cta_secondary.campaign_name = campaign.name || ''
        cta_secondary.campaign_position = campaign.position || ''
        cta_secondary.campaign_creative = campaign.creative || ''

        const data = stringifyForAttribute(cta_secondary)

        return `<nb-cta class="${this.classes.base}__ctaSecondary" data="${data}">${label}</nb-cta>`
    }

    renderLink() {
        const { campaign = {}, copywriting: { popin = {} } = {} } = this.props
        const { id = '', text = '' } = popin

        if (!text) {
            return ''
        }

        return `
            <nb-link
                campaign_id="${campaign.id || ''}"
                campaign_name="${campaign.name || ''}"
                campaign_position="${campaign.position || ''}"
                campaign_creative="${campaign.creative || ''}"
                size="xsmall"
                color="${this.getColor()}"
                popin_id="${id}"
                class="${this.classes.base}__link"
            >${text}</nb-link>
        `
    }

    onOpenPlayer() {
        if (!this.videoPopinElement) {
            this.renderPlayer()
        }
        dispatchEvent({ eventName: EVENT_POPIN_OPEN, args: { id: this.popinId } })
    }

    renderPopin() {
        const { copywriting: { popin = {} } = {} } = this.props
        const {
            id = '',
            image = '',
            heading = '',
            description = '',
            image_alt = '',
            close = ''
        } = popin

        if (!id) {
            return ''
        }

        return `<nb-popin
                    id="${id}"
                    heading="${heading}"
                    variation="next"
                    bgcolor="highlight"
                    image="${image}"
                    image_alt="${image_alt}"
                    label_close="${close}">
                        ${description}
                </nb-popin>`
    }

    onLinkClick() {
        const { copywriting: { popin: { id } } = {} } = this.props

        // Just the first time, we need to build the DOM and trigger the event, after it's all standard
        const fragment = document.createElement('div')
        fragment.innerHTML = this.renderPopin()

        const container = this.querySelector('nb-container')
        if (container) {
            container.appendChild(fragment)
        }

        dispatchEvent({ eventName: EVENT_POPIN_OPEN, args: { id } })

        if (this.link) {
            this.link.removeEventListener('click', this.boundOnLinkClick)
        }
    }

    disconnectedCallback() {
        this.unbindEvents()
    }

    bindEvents() {
        this.videoButton = this.querySelector(`.${this.classes.videoCta}`)
        if (this.videoButton) {
            this.videoButton.addEventListener('click', this.boundOnOpenPlayer)
        }

        this.videoCtaButton = this.querySelector(`.${this.classes.videoCtaIcon}`)
        if (this.videoCtaButton) {
            this.videoCtaButton.addEventListener('click', this.boundOnOpenPlayer)
        }

        this.link = this.querySelector(`nb-link`)
        if (this.link) {
            this.link.addEventListener('click', this.boundOnLinkClick)
        }

        this.videoHTMLEle = this.querySelector(`.${this.classes.videoCta}`)
        if (this.videoHTMLEle) {
            this.videoHTMLEle.addEventListener('click', this.boundhandlePromoClick)
        }

        this.buttonCta = this.querySelector('.nb-hero-banner__cta')
        if (this.buttonCta) {
            this.buttonCta.addEventListener('click', this.boundOnButtonCtaClick)
        }

        this.renderSelfVideoButton = this.querySelector('#video__play__centerButton')
        if (this.renderSelfVideoButton) {
            this.renderSelfVideoButton.addEventListener('click', this.boundOnButtonSelfVideoClick)
        }
    }

    boundOnButtonSelfVideoClick(e) {
        const targetElement = e.target || e.srcElement
        const video = document.getElementById('video_selfHosted')
        const videoControlText = document.querySelector('.sr-only.video_button')

        if (targetElement.dataset.status === 'pause') {
            video.pause()
            targetElement.dataset.status = 'play'
            targetElement.classList.remove('icon-pause')
            targetElement.classList.add('icon-play')
            videoControlText.innerText = 'Play'
        } else {
            video.play()
            targetElement.dataset.status = 'pause'
            targetElement.classList.remove('icon-play')
            targetElement.classList.add('icon-pause')
            videoControlText.innerText = 'Pause'
        }
    }

    onButtonCtaClick() {
        const cta = this.props.copywriting.cta
        if (cta.disabled) return
        dispatchEvent({ eventName: EVENT_CTA_CLICK })
    }

    unbindEvents() {
        if (this.videoButton) {
            this.videoButton.removeEventListener('click', this.boundOnOpenPlayer)
        }

        if (this.link) {
            this.link.removeEventListener('click', this.boundOnLinkClick)
        }

        if (this.videoHTMLEle) {
            this.videoHTMLEle.removeEventListener('click', this.boundhandlePromoClick)
        }

        if (this.buttonCta) {
            this.buttonCta.removeEventListener('click', this.boundOnButtonCtaClick)
        }

        if (this.renderSelfVideoButton) {
            this.renderSelfVideoButton.removeEventListener(
                'click',
                this.boundOnButtonSelfVideoClick
            )
        }
    }

    handleClick() {
        const { campaign } = this.props
        if (!campaign.id) {
            return ''
        }

        let ctaOrPopin = this.props.copywriting.popin ? 'POPIN' : 'CTA'

        handlePromoClick({
            campaign,
            cta_name: `${ctaOrPopin} - ${this.props.copywriting.cta.label}`
        })
    }
}

customElements.get('nb-hero-banner') || customElements.define('nb-hero-banner', HeroBanner)

export default HeroBanner
